import { Injectable, inject } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { ItemCacheService } from '@item-cache';
import { Subject, distinctUntilChanged, switchMap, takeUntil, timer } from 'rxjs';

import { ToasterService } from '#shared/components/toaster/toaster.service';

@Injectable({
  providedIn: 'root',
})
export class ItemCacheSyncToastService {
  private readonly itemCacheService = inject(ItemCacheService);
  private readonly toasterService = inject(ToasterService);

  private readonly warningToastId = 'item-cache-sync-warning';
  private readonly successToastId = 'item-cache-sync-success';
  private readonly cacheSyncWarningToastDelayMs = 5000;

  constructor() {
    const isInitialized$ = toObservable(this.itemCacheService.isInitializedSignal);
    const isSuccessToastActive$ = new Subject<void>();

    timer(this.cacheSyncWarningToastDelayMs)
      .pipe(
        switchMap(() => isInitialized$.pipe(distinctUntilChanged())),
        takeUntil(isSuccessToastActive$),
      )
      .subscribe((isInitialized) => {
        if (!isInitialized) {
          this.showWarning();
        } else if (this.toasterService.isToastActive(this.warningToastId)) {
          this.switchToSuccessToast();
          this.handleSubscription(isSuccessToastActive$);
        } else {
          this.handleSubscription(isSuccessToastActive$);
        }
      });
  }

  private showWarning(): void {
    const syncMessage = $localize`Syncing data. This might take a while. Page can be closed to continue in the background.`;
    this.toasterService.showWarning(syncMessage, this.warningToastId, null);
  }

  private switchToSuccessToast(): void {
    this.toasterService.hide(this.warningToastId);
    const successMessage = $localize`Data sync completed.`;
    this.toasterService.showSuccess(successMessage, this.successToastId, 5000);
  }

  private handleSubscription(isSuccessToastActive$: Subject<void>): void {
    isSuccessToastActive$.next();
    isSuccessToastActive$.complete();
  }
}
